import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { isCreativeReviewDept } from "@/lib/dept-helpers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { TaskUpdateModal } from "@/components/editor/TaskUpdateModal";
import type { EditorVideoSubmission } from "@/lib/editor-tasks";
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/corrections")({
  component: CorrectionsPage,
});

interface Row {
  id: string;
  creative_id: string | null;
  date: string;
  product: string | null;
  video_type: string | null;
  state: "correction" | "changes_requested";
  reviewer_note: string | null;
  table: "editor_video_submissions" | "designer_submissions" | "ai_submissions";
  raw: EditorVideoSubmission;
}

function CorrectionsPage() {
  const { user, profile } = useAuth();
  const dept = profile?.department;
  const navigate = useNavigate();

  const isEditor = dept === "editor";
  const isDesigner = dept === "graphic_designer";
  const isAi = dept === "ai";

  useEffect(() => {
    if (profile && !isCreativeReviewDept(profile.department)) {
      toast.info("This section is only for Editor, Graphic Designer and AI submissions.");
      navigate({ to: "/update-task" });
    }
  }, [profile, navigate]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["my-corrections", user?.id, dept],
    enabled: !!user && (isEditor || isDesigner || isAi),
    queryFn: async () => {
      const table = isEditor ? "editor_video_submissions" : isAi ? "ai_submissions" : "designer_submissions";
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .eq("user_id", user!.id)
        .is("deleted_at", null)
        .or("correction_status.eq.fix,and(review_status.eq.changes_requested,acknowledged_at.is.null)")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const tableName: "editor_video_submissions" | "designer_submissions" | "ai_submissions" =
    isEditor ? "editor_video_submissions" : isAi ? "ai_submissions" : "designer_submissions";

  const mapped: Row[] = useMemo(() => {
    return rows.map((r: any) => ({
      id: r.id,
      creative_id: r.creative_id ?? null,
      date: r.submission_date,
      product: r.product_name ?? r.creative_description ?? null,
      video_type: r.video_type ?? r.type ?? null,
      state: r.correction_status === "fix" ? "correction" : "changes_requested",
      reviewer_note: r.correction_note ?? null,
      table: tableName,
      raw: r as EditorVideoSubmission,
    }));
  }, [rows, tableName]);

  const qc = useQueryClient();
  const ackMut = useMutation({
    mutationFn: async (row: Row) => {
      const { error } = await supabase
        .from(row.table)
        .update({ acknowledged_at: new Date().toISOString() } as never)
        .eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Acknowledged");
      qc.invalidateQueries({ queryKey: ["my-corrections"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editing, setEditing] = useState<EditorVideoSubmission | null>(null);

  if (!isEditor && !isDesigner && !isAi) {
    return (
      <>
        <AppHeader subtitle="Fix items flagged by your reviewer" />
        <div className="p-6">
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Corrections are not enabled for your department.
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader subtitle="Fix items flagged by your reviewer" />
      <div className="p-6">
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Creative ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  {isEditor && <TableHead>Video Type</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Reviewer Note</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isEditor ? 7 : 6} className="text-center text-muted-foreground py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : mapped.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isEditor ? 7 : 6} className="text-center text-muted-foreground py-10">
                      ✅ All clear! No items need fixing right now.
                    </TableCell>
                  </TableRow>
                ) : (
                  mapped.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.creative_id || "—"}</TableCell>
                      <TableCell>{r.date ? formatIst(r.date, "MMM d, yyyy") : "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate">{r.product || "—"}</TableCell>
                      {isEditor && <TableCell>{r.video_type || "—"}</TableCell>}
                      <TableCell>
                        {r.state === "correction" ? (
                          <Badge variant="destructive">Correction Required</Badge>
                        ) : (
                          <Badge className="bg-blue-600 hover:bg-blue-700">Changes</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px] text-xs text-muted-foreground">
                        {r.reviewer_note || <span className="italic">No note</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.state === "changes_requested" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={ackMut.isPending}
                            onClick={() => ackMut.mutate(r)}
                          >
                            <Check className="mr-1 h-3.5 w-3.5" /> Acknowledge
                          </Button>
                        ) : isEditor ? (
                          <Button size="sm" onClick={() => setEditing(r.raw)}>
                            Update & Resubmit <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Open in Submissions</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {isEditor && (
        <TaskUpdateModal
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          editingVideo={editing}
          editWindowHours={24 * 7}
          editWindowLabel="7 days"
        />
      )}

    </>
  );
}
